import React, { useState, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import SEOHead from '../components/SEOHead';
import { BrandFormShell, FormSection } from '../components/BrandFormShell';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const { isLoggedIn, logout, user } = useContext(AuthContext);
  const navigate = useNavigate();

  const adminToken = localStorage.getItem('adminAuthToken');
  const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
  const isAdminLoggedIn = !!adminToken && adminUser.role;

  const getDashboardLink = () => isAdminLoggedIn ? '/admin' : '/dashboard';
  const getDashboardLabel = () => {
    if (isAdminLoggedIn) return 'Admin Panel';
    return user?.name ? `Hi, ${user.name.split(' ')[0]}` : 'Dashboard';
  };

  const handleLogout = () => {
    localStorage.removeItem('adminAuthToken');
    localStorage.removeItem('adminUser');
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    logout();
    navigate('/', { replace: true });
    setTimeout(() => window.location.reload(), 100);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    if (!form.name.trim()) { setSubmitError('Please enter your name.'); return; }
    if (!form.email.trim()) { setSubmitError('Please enter your email address.'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) { setSubmitError('Please enter a valid email address.'); return; }
    if (!form.message.trim() || form.message.trim().length < 10) { setSubmitError('Please enter a message (at least 10 characters).'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/contact`, {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone?.trim() || undefined,
        message: form.message.trim(),
      });
      setSubmitted(true);
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'We could not send your message right now. Please try again.';
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEOHead title="Contact LiteRent | Rent Verified Homes in Ibadan" description="Contact LiteRent to learn how eligible renters in Ibadan can move into verified homes with 30% upfront and spread the balance over time." canonical="https://literent.com.ng/contact" />
    <main className="min-h-screen bg-[#fdfdfd] text-[#1a1a1a] flex flex-col">

      {/* ══ HEADER ══ */}
      <header className="sticky top-0 z-50 bg-white border-b border-[#afc09e] shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex-shrink-0">
              <img
                src="/Logo.jpg"
                alt="LiteRent Logo"
                className="h-12 sm:h-14 md:h-16 w-auto object-contain"
                onError={(e) => { e.target.src = 'https://via.placeholder.com/180x60?text=LiteRent'; }}
              />
            </Link>

            <nav className="hidden lg:flex items-center space-x-8 xl:space-x-10">
              <Link to="/" className="text-[#294c79] hover:text-[#1f3a5c] font-medium transition-colors">Home</Link>
              <Link to="/#how-it-works" className="text-[#294c79] hover:text-[#1f3a5c] font-medium transition-colors">How It Works</Link>
              <Link to="/browse" className="text-[#294c79] hover:text-[#1f3a5c] font-medium transition-colors">Browse Houses</Link>
              <Link to="/about" className="text-[#294c79] hover:text-[#1f3a5c] font-medium transition-colors">About Us</Link>
              <Link to="/contact" className="text-[#a5c250] font-semibold">Contact Us</Link>
              <div className="flex items-center space-x-4">
                {(isLoggedIn || isAdminLoggedIn) ? (
                  <>
                    <Link to={getDashboardLink()}>
                      <button className={`px-6 py-2.5 text-white rounded-full font-medium transition shadow-sm ${isAdminLoggedIn ? 'bg-red-600 hover:bg-red-700' : 'bg-[#294c79] hover:bg-[#1f3a5c]'}`}>
                        {getDashboardLabel()}
                      </button>
                    </Link>
                    <button onClick={handleLogout} className="px-6 py-2.5 border-2 border-red-500 text-red-600 rounded-full font-medium hover:bg-red-50 transition">
                      Logout
                    </button>
                  </>
                ) : (
                  <>
                    <Link to="/login"><button className="px-6 py-2.5 border-2 border-[#294c79] text-[#294c79] rounded-full font-medium hover:bg-[#294c79]/10 transition">Login</button></Link>
                    <Link to="/signup"><button className="px-6 py-2.5 bg-[#a5c250] text-white rounded-full font-medium hover:bg-[#8bb83a] shadow-sm transition">Get Started</button></Link>
                  </>
                )}
              </div>
            </nav>

            <button
              className="lg:hidden p-2 rounded-md text-[#294c79] hover:bg-gray-100 focus:outline-none"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
            >
              <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isMenuOpen
                  ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />}
              </svg>
            </button>
          </div>
        </div>

        {isMenuOpen && (
          <div className="lg:hidden bg-white border-t border-[#afc09e] py-6 px-5">
            <div className="flex flex-col space-y-5">
              <Link to="/" className="text-[#294c79] font-medium py-3 text-lg" onClick={() => setIsMenuOpen(false)}>Home</Link>
              <Link to="/#how-it-works" className="text-[#294c79] font-medium py-3 text-lg" onClick={() => setIsMenuOpen(false)}>How It Works</Link>
              <Link to="/browse" className="text-[#294c79] font-medium py-3 text-lg" onClick={() => setIsMenuOpen(false)}>Browse Houses</Link>
              <Link to="/about" className="text-[#294c79] font-medium py-3 text-lg" onClick={() => setIsMenuOpen(false)}>About Us</Link>
              <Link to="/contact" className="text-[#a5c250] font-semibold py-3 text-lg" onClick={() => setIsMenuOpen(false)}>Contact Us</Link>
              <div className="pt-6 border-t border-gray-200 flex flex-col space-y-4">
                {(isLoggedIn || isAdminLoggedIn) ? (
                  <>
                    <Link to={getDashboardLink()} onClick={() => setIsMenuOpen(false)}>
                      <button className={`w-full py-4 text-white rounded-full font-medium text-lg ${isAdminLoggedIn ? 'bg-red-600' : 'bg-[#294c79]'}`}>{getDashboardLabel()}</button>
                    </Link>
                    <button onClick={() => { handleLogout(); setIsMenuOpen(false); }} className="w-full py-4 border-2 border-red-500 text-red-600 rounded-full font-medium text-lg">Logout</button>
                  </>
                ) : (
                  <>
                    <Link to="/login" onClick={() => setIsMenuOpen(false)}><button className="w-full py-4 border-2 border-[#294c79] text-[#294c79] rounded-full font-medium text-lg">Login</button></Link>
                    <Link to="/signup" onClick={() => setIsMenuOpen(false)}><button className="w-full py-4 bg-[#a5c250] text-white rounded-full font-medium text-lg">Get Started</button></Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </header>

      {/* ══ HERO BANNER ══ */}
      <section className="bg-[#294c79] py-16 sm:py-20 px-5 sm:px-8 relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-72 h-72 bg-[#a5c250]/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-16 -left-16 w-56 h-56 bg-white/5 rounded-full blur-2xl pointer-events-none" />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <span className="inline-block bg-[#a5c250]/20 border border-[#a5c250]/30 text-[#c8e87a] text-xs font-semibold px-4 py-2 rounded-full tracking-widest uppercase mb-5">
            Get In Touch
          </span>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white leading-tight mb-4">
            We are here to help.
          </h1>
          <p className="text-white/70 text-lg sm:text-xl max-w-xl mx-auto">
            Whether you have a question about a listing, need help with your application, or just want to know more about how LiteRent works — we want to hear from you.
          </p>
        </div>
      </section>

      {/* ══ CONTACT CARDS + FORM ══ */}
      <section className="flex-1 py-16 sm:py-24 px-5 sm:px-8">
        <div className="max-w-5xl mx-auto">

          {/* Contact method cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-14">
            {/* WhatsApp */}
            <a
              href="https://wa.me/2349057582412"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-start gap-5 bg-white border border-[#e8f2d1] rounded-2xl p-7 shadow-sm hover:shadow-lg hover:border-[#a5c250]/50 transition-all duration-300"
            >
              <div className="w-14 h-14 flex-shrink-0 rounded-full bg-[#25D366]/10 flex items-center justify-center text-2xl group-hover:scale-110 transition-transform">
                💬
              </div>
              <div>
                <p className="font-semibold text-[#294c79] text-lg mb-1">Chat on WhatsApp</p>
                <p className="text-[#1a1a1a]/60 text-sm mb-3">Fastest response. We typically reply within minutes during business hours.</p>
                <p className="text-[#25D366] font-medium">+234 905 758 2412</p>
              </div>
            </a>

            {/* Email */}
            <a
              href="mailto:support@literent.com.ng"
              className="group flex items-start gap-5 bg-white border border-[#e8f2d1] rounded-2xl p-7 shadow-sm hover:shadow-lg hover:border-[#a5c250]/50 transition-all duration-300"
            >
              <div className="w-14 h-14 flex-shrink-0 rounded-full bg-[#294c79]/10 flex items-center justify-center text-2xl group-hover:scale-110 transition-transform">
                📧
              </div>
              <div>
                <p className="font-semibold text-[#294c79] text-lg mb-1">Send an Email</p>
                <p className="text-[#1a1a1a]/60 text-sm mb-3">For detailed enquiries, documents, or anything that needs a paper trail.</p>
                <p className="text-[#294c79] font-medium">support@literent.com.ng</p>
              </div>
            </a>
          </div>

          {/* Divider */}
          <div className="flex items-center gap-4 mb-14">
            <div className="flex-1 h-px bg-[#e8f2d1]" />
            <span className="text-[#1a1a1a]/40 text-sm font-medium px-2">Or send us a message directly</span>
            <div className="flex-1 h-px bg-[#e8f2d1]" />
          </div>

          {/* Contact Form */}
          <div className="max-w-2xl mx-auto">
            {submitted ? (
              <div className="bg-[#e8f2d1] border border-[#a5c250]/30 rounded-2xl p-12 text-center">
                <div className="text-5xl mb-4">✅</div>
                <p className="text-2xl font-bold text-[#294c79] mb-3">Message Sent!</p>
                <p className="text-[#1a1a1a]/70 text-lg mb-8">
                  Thanks for reaching out, <strong>{form.name}</strong>! Our team will get back to you at <strong>{form.email}</strong> within 24 hours on business days.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    onClick={() => { setSubmitted(false); setForm({ name: '', email: '', phone: '', message: '' }); }}
                    className="px-8 py-3 border-2 border-[#294c79] text-[#294c79] rounded-full font-medium hover:bg-[#294c79]/10 transition"
                  >
                    Send Another
                  </button>
                  <Link to="/" className="px-8 py-3 bg-[#294c79] text-white rounded-full font-medium hover:bg-[#1f3a5c] transition text-center">
                    Back to Home
                  </Link>
                </div>
              </div>
            ) : (
              <BrandFormShell
                badge="Contact LiteRent"
                title="Send your message the easy way"
                subtitle="Tell us what you need, and the LiteRent team will get back to you. Keep it simple and clear — one good message is enough."
              >
                <FormSection title="Your message" description="Use the details below so we can respond properly.">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {submitError ? (
                      <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{submitError}</div>
                    ) : null}
                    <div>
                      <label className="block text-sm font-semibold text-[#294c79] mb-2">Full Name</label>
                      <input type="text" required placeholder="e.g. Bolu Adeyemi" className="w-full px-5 py-4 rounded-2xl border border-[#d9e5ef] focus:outline-none focus:ring-4 focus:ring-[#294c79]/10 focus:border-[#294c79] transition" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#294c79] mb-2">Email Address</label>
                      <input type="email" required placeholder="e.g. bolu@email.com" className="w-full px-5 py-4 rounded-2xl border border-[#d9e5ef] focus:outline-none focus:ring-4 focus:ring-[#294c79]/10 focus:border-[#294c79] transition" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#294c79] mb-2">Phone Number <span className="text-gray-400 font-normal">(optional)</span></label>
                      <input type="tel" placeholder="e.g. 09057582412" className="w-full px-5 py-4 rounded-2xl border border-[#d9e5ef] focus:outline-none focus:ring-4 focus:ring-[#294c79]/10 focus:border-[#294c79] transition" value={form.phone || ''} onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, '').slice(0, 14) })} />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#294c79] mb-2">Message</label>
                      <textarea rows={5} required placeholder="Tell us what you need help with..." className="w-full px-5 py-4 rounded-2xl border border-[#d9e5ef] focus:outline-none focus:ring-4 focus:ring-[#294c79]/10 focus:border-[#294c79] resize-none transition" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
                    </div>
                    <button type="submit" disabled={submitting} className={`w-full py-4 rounded-2xl font-semibold text-lg transition shadow-md ${submitting ? 'bg-gray-400 cursor-not-allowed text-white' : 'bg-[#294c79] text-white hover:bg-[#1f3a5c]'}`}>
                      {submitting ? 'Sending...' : 'Send Message →'}
                    </button>
                    <p className="text-center text-[#1a1a1a]/40 text-sm">We respond within 24 hours on business days.</p>
                  </form>
                </FormSection>
              </BrandFormShell>
            )}
          </div>

        </div>
      </section>

      <Footer />

    </main>
      </>
  );
}